import React, { useEffect, useState } from "react";
import PageHero from "../components/dental/PageHero";
import SectionTitle from "../components/dental/SectionTitle";
import Button from "../components/dental/Button";
import { ArrowLeft, ArrowRight, Camera, ScanLine, Eye, Sparkles } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { IMAGES } from "@/data/site";

const steps = [
  {
    icon: Camera,
    title: "Capture",
    desc: "High-resolution photos and 3D intra-oral scans of your existing smile.",
  },
  {
    icon: ScanLine,
    title: "Design",
    desc: "We design your new smile digitally, refining shape, shade and proportions.",
  },
  {
    icon: Eye,
    title: "Preview",
    desc: "You see and approve a virtual mock-up before any treatment begins.",
  },
  {
    icon: Sparkles,
    title: "Reveal",
    desc: "We deliver your final restorations with precision and artistry.",
  },
];

const videos = [
  { id: 1, vimeoId: "749244473", title: "Smile Transformation 1" },
  { id: 2, vimeoId: "749244445", title: "Smile Transformation 2" },
  { id: 3, vimeoId: "749244416", title: "Smile Transformation 3" },
  { id: 4, vimeoId: "749244309", title: "Smile Transformation 4" },
  { id: 5, vimeoId: "749244516", title: "Smile Transformation 5" },
];

const VideoCard = ({ vimeoId, title }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="w-[420px] border-2 border-[#B79A68]/40 hover:border-[#B79A68] transition-colors shadow-[0_0_40px_-10px_rgba(183,154,104,0.28)]">
        <div className="w-full aspect-square overflow-hidden bg-black">
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&badge=0`}
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={title}
          />
        </div>
      </div>
      <p className="mt-6 font-cinzel text-sm text-[#4E5750]/80 uppercase tracking-[0.1em] text-center">
        {title}
      </p>
    </div>
  );
};

const VideoSlider = ({ videos }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return undefined;

    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  if (!videos.length) {
    return null;
  }

  return (
    <div className="relative mt-6 md:mt-0 max-w-[560px] mx-auto px-10">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-8">
          {videos.map((video) => (
            <div
              key={video.id}
              className="min-w-0 flex-[0_0_100%] flex justify-center"
            >
              <VideoCard vimeoId={video.vimeoId} title={video.title} />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => emblaApi?.scrollPrev()}
        disabled={!canScrollPrev}
        aria-label="Previous videos"
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex h-11 w-11 items-center justify-center rounded-full border border-[#B79A68]/40 bg-[#FBF8F1] text-[#6B4F0E] shadow-lg transition-colors disabled:opacity-40"
      >
        <ArrowLeft size={18} />
      </button>

      <button
        type="button"
        onClick={() => emblaApi?.scrollNext()}
        disabled={!canScrollNext}
        aria-label="Next videos"
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex h-11 w-11 items-center justify-center rounded-full border border-[#B79A68]/40 bg-[#FBF8F1] text-[#6B4F0E] shadow-lg transition-colors disabled:opacity-40"
      >
        <ArrowRight size={18} />
      </button>

      <div className="mt-5 flex items-center justify-center">
        <span className="font-cinzel text-sm tracking-[0.18em] uppercase text-[#6B4F0E]/80">
          {selectedIndex + 1}/{videos.length}
        </span>
      </div>
    </div>
  );
};

const DigitalSmile = () => {
  return (
  <>
    <PageHero
      subtitle="Plan Your Perfect"
      title="Digital Smile Design"
      breadcrumb="Digital Smile Design"
      image="https://images.pexels.com/photos/9951391/pexels-photo-9951391.jpeg"
    />

    <section className="bg-[#FBF8F1] py-10 ">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <SectionTitle
            script=""
            title="What Is Digital Smile Design?"
            subtitle=""
            align="left"
          />
          <div className="mt-6 space-y-4 text-[#4E5750]/85 leading-relaxed">
            <p>
              Digital Smile Design (DSD) is a treatment planning protocol that
              allows for careful analysis of the patient’s facial and dental
              structures through state-of-the-art videography and digital
              technology. Today, the DSD Concept is considered the most modern,
              patient-centered design approach available.
            </p>
            <p>
              DSD Clinics are an exclusive group of hand-selected practices all
              over the world that share a passion for combining dentistry,
              aesthetics, and technology in their pursuit of excellence through
              the DSD Concept. DSD Clinics are focused on making the Digital
              Smile Design process a personalized experience for the patient,
              allowing Smile Design to be truly customized to their unique
              specifications.
            </p>
            <p>
              Through the use of the latest digital technology and the
              collaboration of an exclusive group of partners, DSD Clinics offer
              the highest quality standards of Smile Design and patient care.
            </p>
          </div>
          
        </div>
        <div className="relative bg-[#F1E4CC] p-3 md:p-4">
          <div className="absolute -top-4 -left-4 w-24 h-24 border border-[#B79A68]/60 z-0" />
          <img
            src={IMAGES.digital_smiles}
            alt="Digital Smile Design"
            className="relative w-full h-[500px]  object-cover z-10"
          />
          <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-[#B79A68]/60 z-0" />
        </div>
      </div>
    </section>

    {/* <section className="bg-[#ffffff] py-20 border-t border-[#c8a878]/15">
      <div className="max-w-7xl mx-auto px-6">
        <SectionTitle
          script="How It"
          title="Works"
          subtitle="The 4-Step Journey"
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {steps.map(({ icon: Icon, title, desc }, idx) => (
            <div
              key={title}
              className="relative bg-[#ffffff] p-7 text-center border border-[#c8a878]/30"
            >
              <span className="absolute top-3 right-4 font-cinzel gold-gradient-text text-2xl opacity-50">
                0{idx + 1}
              </span>
              <div className="w-14 h-14 rounded-full border border-[#c8a878] flex items-center justify-center mx-auto mb-4 text-[#6B4F0E]">
                <Icon size={22} />
              </div>
              <h3 className="font-cinzel text-sm gold-gradient-text uppercase tracking-[0.15em] mb-3">
                {title}
              </h3>
              <p className="text-[#5a4d35]/80 text-sm leading-relaxed">
                {desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section> */}

    <section className="bg-[#F4ECE0] py-5">
      <div className="max-w-7xl mx-auto px-6">
        <SectionTitle
          script="See Our"
          title="Digital Smile Simulation"
          subtitle="Real Transformations"
        />
        <div className="mt-16">
          <VideoSlider videos={videos} />
        </div>
      </div>
    </section>
  </>
  );
};

export default DigitalSmile;
